/**
 * Checkout delivery vs collection card selector.
 */

import { applyCheckoutDeliveryMethod } from './checkout-fields.js';

function getConfig() {
	return window.doneCommerce || {};
}

async function apiFetch(path, options = {}) {
	const config = getConfig();
	const response = await fetch(`${config.restUrl}${path}`, {
		...options,
		headers: {
			'Content-Type': 'application/json',
			'X-WP-Nonce': config.nonce,
			...(options.headers || {}),
		},
	});

	if (!response.ok) {
		throw new Error('Request failed');
	}

	return response.json();
}

function checkoutForm() {
	return document.querySelector('[data-dc-checkout], form.checkout');
}

/**
 * Always post the fulfilment choice (radios are UI-only so a missing checked
 * state cannot drop dc_delivery_method from the place-order payload).
 */
function syncDeliveryMethodInput(method) {
	const form = checkoutForm();
	if (!form) {
		return;
	}

	let hidden = form.querySelector('input[data-dc-delivery-method]');
	if (!hidden) {
		hidden = document.createElement('input');
		hidden.type = 'hidden';
		hidden.name = 'dc_delivery_method';
		hidden.setAttribute('data-dc-delivery-method', '');
		form.appendChild(hidden);
	}

	hidden.value = method === 'collection' || method === 'delivery' ? method : '';
}

/**
 * Keep Woo's chosen shipping method in the place-order payload (native radios are CSS-hidden).
 */
export function syncShippingMethodInput(rateId) {
	const form = checkoutForm();
	if (!form) {
		return;
	}

	let input = form.querySelector('input[data-dc-shipping-method]');
	if (!rateId) {
		input?.remove();
		return;
	}

	if (!input) {
		input = document.createElement('input');
		input.type = 'hidden';
		input.name = 'shipping_method[0]';
		input.setAttribute('data-dc-shipping-method', '');
		form.appendChild(input);
	}

	input.value = String(rateId);
}

/**
 * One shipping_method[0] in the place-order payload.
 *
 * Woo's radios are copied here, then unnamed, so a stale hidden id cannot
 * override the rate the order review just rendered.
 */
export function adoptCheckedShippingMethod() {
	const form = checkoutForm();
	if (!form) {
		return;
	}

	const radios = form.querySelectorAll('input.shipping_method');
	if (!radios.length) {
		// The fresh order review has no rate. Drop a previous hidden id so it cannot be posted.
		syncShippingMethodInput('');
		return;
	}

	const checked = form.querySelector('input.shipping_method:checked') || radios[0];
	syncShippingMethodInput(checked?.value || '');
	radios.forEach((radio) => {
		radio.removeAttribute('name');
	});
}

function collectionDescription() {
	const cc = getConfig().clickCollect || {};
	const i18n = getConfig().i18n || {};
	const prep = String(cc.preparationTime || '').trim();
	if (prep) {
		const template = i18n.collectionCardDesc || 'Usually ready within %s during store hours • We’ll notify you when ready.';
		return template.replace('%s', prep);
	}
	return i18n.collectionCardDescNoTime || 'We’ll notify you when ready.';
}

function cardIcon(id) {
	if (id === 'collection') {
		// Flat storefront / pickup icon
		return `<svg class="dc-delivery-card__icon" xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 10.5 12 3l9 7.5"/><path d="M5 9.5V20h14V9.5"/><path d="M9 20v-6h6v6"/></svg>`;
	}

	// Flat delivery truck icon
	return `<svg class="dc-delivery-card__icon" xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 7h11v10H3z"/><path d="M14 10h4l3 3v4h-7"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/></svg>`;
}

function renderCards(container, options) {
	const cards = [
		{
			id: 'collection',
			title: 'FREE Click & Collect',
			desc: collectionDescription(),
			enabled: options.collection !== false,
		},
		{
			id: 'delivery',
			title: 'Delivery',
			desc: getConfig().i18n?.deliveryCardDesc || 'Australia-wide shipping • Delivery costs calculated by weight at checkout.',
			enabled: options.delivery !== false,
		},
	];

	container.innerHTML = cards
		.filter((card) => card.enabled)
		.map(
			(card) => `
		<label class="dc-delivery-card" data-delivery-card="${card.id}">
			<input type="radio" name="dc_delivery_method_ui" value="${card.id}">
			<span class="dc-delivery-card__icon-wrap">${cardIcon(card.id)}</span>
			<span class="dc-delivery-card__body">
				<span class="dc-delivery-card__title">${card.title}</span>
				<span class="dc-delivery-card__desc">${card.desc}</span>
			</span>
		</label>
	`
		)
		.join('');
}

function updateDeliveryNotice(container, cartMeta) {
	let notice = container.parentElement?.querySelector('[data-delivery-notice]');

	if (!notice) {
		notice = document.createElement('p');
		notice.className = 'dc-delivery-notice';
		notice.setAttribute('data-delivery-notice', '');
		container.parentElement?.insertBefore(notice, container.nextSibling);
	}

	const config = getConfig();
	let message = '';

	if (cartMeta?.has_collection_only) {
		message =
			config.i18n?.collectionOnlyNotice ||
			'One or more items in your order are available for Click & Collect only.';
	} else if (cartMeta?.unknown_weight) {
		message =
			config.i18n?.unknownWeightNotice ||
			'Delivery is not available until product weights are set.';
	} else if (cartMeta?.delivery_available === false) {
		message =
			config.i18n?.deliveryUnavailableNotice ||
			'Delivery is not available for your cart weight.';
	}

	if (message) {
		notice.textContent = message;
		notice.hidden = false;
	} else {
		notice.textContent = '';
		notice.hidden = true;
	}
}

function showFulfilmentRetry(container, onRetry) {
	let notice = container.parentElement?.querySelector('[data-delivery-notice]');
	if (!notice) {
		notice = document.createElement('p');
		notice.className = 'dc-delivery-notice';
		notice.setAttribute('data-delivery-notice', '');
		container.parentElement?.insertBefore(notice, container.nextSibling);
	}

	const i18n = getConfig().i18n || {};
	notice.textContent = '';
	const text = document.createElement('span');
	text.textContent = i18n.fulfilmentRetry || 'We couldn’t refresh fulfilment.';
	const button = document.createElement('button');
	button.type = 'button';
	button.className = 'dc-delivery-notice__retry';
	button.textContent = i18n.tryAgain || 'Try again';
	button.addEventListener('click', onRetry);
	notice.append(text, document.createTextNode(' '), button);
	notice.hidden = false;
}

function setActive(container, method) {
	container.querySelectorAll('.dc-delivery-card').forEach((card) => {
		const input = card.querySelector('input');
		const active = input?.value === method;
		card.classList.toggle('is-active', active);
		if (input) {
			input.checked = active;
			if (active) {
				input.setAttribute('checked', 'checked');
			} else {
				input.removeAttribute('checked');
			}
		}
	});
	syncDeliveryMethodInput(method);
}

/**
 * Reload payment and totals. Pending delivery cost is applied after the fragment returns.
 */
export function triggerCheckoutUpdate() {
	if (typeof window.jQuery !== 'undefined') {
		window.jQuery(document.body).trigger('update_checkout');
		return;
	}
	document.dispatchEvent(new CustomEvent('dc:quiet-delivery-cost'));
}

function announceMethod(method) {
	applyCheckoutDeliveryMethod(method);
	syncDeliveryMethodInput(method);
	document.dispatchEvent(
		new CustomEvent('dc:delivery-method', {
			detail: { method },
		})
	);
}

export function initCheckoutDelivery() {
	const container = document.querySelector('[data-delivery-cards]');

	if (!container) {
		return;
	}

	const load = async () => {
		try {
			const availability = await apiFetch('done-commerce/v1/checkout/delivery-method');
			const options = availability.options || availability.available || availability;
			const cartMeta = availability.cart_meta || {};

			renderCards(container, options);
			updateDeliveryNotice(container, cartMeta);

			const current =
				availability.method ||
				(options.delivery === false && options.collection !== false ? 'collection' : '');
			setActive(container, current);
			syncShippingMethodInput(availability.chosen_rate_id || '');
			if (current) {
				announceMethod(current);
				triggerCheckoutUpdate();
			}
		} catch {
			renderCards(container, { delivery: true, collection: true });
			const method = checkoutForm()?.getAttribute('data-delivery-method') || '';
			if (method === 'collection' || method === 'delivery') {
				setActive(container, method);
				announceMethod(method);
			}
			adoptCheckedShippingMethod();
			showFulfilmentRetry(container, () => {
				load();
			});
		}
	};

	container.addEventListener('change', async (e) => {
		const input = e.target.closest('input[name="dc_delivery_method_ui"]');
		if (!input) {
			return;
		}

		setActive(container, input.value);
		announceMethod(input.value);

		try {
			const result = await apiFetch('done-commerce/v1/checkout/delivery-method', {
				method: 'POST',
				body: JSON.stringify({ method: input.value }),
			});
			syncShippingMethodInput(result.chosen_rate_id || '');
		} catch {
			// update_checkout still posts dc_delivery_method and refreshes gateways.
		}

		triggerCheckoutUpdate();
	});

	const onUpdated = () => {
		adoptCheckedShippingMethod();
	};
	if (typeof window.jQuery !== 'undefined') {
		window.jQuery(document.body).on('updated_checkout', onUpdated);
	}
	document.body.addEventListener('updated_checkout', onUpdated);

	load();
}
